import { Box, Grid, Skeleton, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';

const formatCurrency = (value: number, currency: string) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ` ${currency}`;

const formatShort = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value.toFixed(0);
};

const PaymentBreakdownPanel = ({
  title,
  helpKey,
  data,
  dataKey,
  color,
  currency,
}: {
  title: string;
  helpKey: string;
  data: { amount: number }[];
  dataKey: string;
  color: string;
  currency: string;
}) => {
  const { t } = useTranslation();

  return (
    <DashboardWidget title={title} helpKey={helpKey} chartHeight={300}>
      {data.length > 0 ? (
        <DashboardSizedChart height={300}>
          <BarChart data={data.slice(0, 10)} layout="vertical" margin={{ left: 20, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" tickFormatter={formatShort} />
            <YAxis type="category" dataKey={dataKey} width={100} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value: number) => formatCurrency(value, currency)} />
            <Bar dataKey="amount" fill={color} radius={[0, 4, 4, 0]} />
          </BarChart>
        </DashboardSizedChart>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
          <Typography variant="body2">{t('common:dashboard.noData')}</Typography>
        </Box>
      )}
    </DashboardWidget>
  );
};

const PaymentBreakdown = () => {
  const { paymentsByUser, paymentsByDepot, paymentTypes, sectionLoading, currency } = useDashboard();
  const { t } = useTranslation();

  if (sectionLoading.payments) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2].map((idx) => (
          <Grid key={idx} size={{ xs: 12, md: 4 }}>
            <DashboardWidget chartHeight={300}>
              <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
            </DashboardWidget>
          </Grid>
        ))}
      </Grid>
    );
  }

  const panels = [
    {
      title: t('common:dashboard.payments.byUser'),
      helpKey: 'payments.byUser',
      data: paymentsByUser,
      dataKey: 'user',
      color: '#0288d1',
    },
    {
      title: t('common:dashboard.payments.byDepot'),
      helpKey: 'payments.byDepot',
      data: paymentsByDepot,
      dataKey: 'depot',
      color: '#2e7d32',
    },
    {
      title: t('common:dashboard.payments.byType'),
      helpKey: 'payments.byType',
      data: paymentTypes,
      dataKey: 'type',
      color: '#ed6c02',
    },
  ] as const;

  return (
    <Grid container spacing={2}>
      {panels.map((panel) => (
        <Grid key={panel.dataKey} size={{ xs: 12, md: 4 }}>
          <PaymentBreakdownPanel {...panel} currency={currency} />
        </Grid>
      ))}
    </Grid>
  );
};

export default PaymentBreakdown;